/**
 * Callback form shown after a visitor picks an AI persona on /for-investors.
 *
 * Collects first name + phone, then a TWO-checkbox TCPA consent block
 * (mirrors the mens-health VitamdStart pattern):
 *   - callConsent — REQUIRED (blocks submit; carrier-standard "Reply STOP")
 *   - textConsent — OPTIONAL, separate opt-in
 *
 * On submit it POSTs { persona_id, name, phone, call_consent, text_consent,
 * consent_at } to POST /api/v1/voice/request-callback. The backend gates the
 * actual AlfredVoice originate behind config and checks ConsentGuard /
 * suppression before dialing, so a 503 ("not provisioned") or 403
 * ("opted out") is an expected, handled state — surfaced as an error here.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import type { InvestorPersona } from '../data/investorPersonas';
import { VOICE_CALLBACK_CALLER_ID } from '../data/investorPersonas';

interface Props {
  persona: InvestorPersona;
  onBack: () => void;
}

// US 10-digit or E.164 (+1…). Mirrors the backend _normalize_us_e164 check.
const PHONE_RE = /^(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const PersonaCallbackForm: React.FC<Props> = ({ persona, onBack }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [callConsent, setCallConsent] = useState(false);
  const [textConsent, setTextConsent] = useState(false);

  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Per-field validation, only surfaced after a submit attempt.
  const [touched, setTouched] = useState(false);

  const nameValid = name.trim().length > 0;
  const phoneValid = PHONE_RE.test(phone.trim());
  const canSubmit = nameValid && phoneValid && callConsent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setErrorMsg(null);
    if (!canSubmit) return;

    setState('submitting');
    try {
      await api.post('/voice/request-callback', {
        persona_id: persona.id,
        name: name.trim(),
        phone: phone.trim(),
        call_consent: callConsent,
        text_consent: textConsent,
        consent_at: new Date().toISOString(),
      });
      setState('success');
    } catch (err: any) {
      // FastAPI puts the human message in response.data.detail (the axios
      // response interceptor normalises array-form validation errors).
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong. Please try the chat above instead.';
      setErrorMsg(typeof detail === 'string' ? detail : 'Please check your details and try again.');
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-apple-divider bg-white p-7 shadow-card"
      >
        <CheckCircle2 className="h-9 w-9 text-emerald-500" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-semibold tracking-tight text-apple-ink">
          {persona.name} will call you shortly.
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-apple-subtext">
          Keep your phone close — you'll see an incoming call from{' '}
          <span className="font-medium text-apple-ink">{VOICE_CALLBACK_CALLER_ID}</span>.
          {persona.name} will introduce themselves and walk through how VentureOS would
          handle your pipeline.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-apple-blue hover:text-apple-blueHover"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Pick a different person
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      noValidate
      className="rounded-3xl border border-apple-divider bg-white p-7 text-left shadow-card"
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm font-medium text-apple-subtext hover:text-apple-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back
      </button>

      <h3 className="mt-4 text-2xl font-semibold tracking-tight text-apple-ink">
        Have {persona.name} call you
      </h3>
      <p className="mt-2 text-sm text-apple-subtext">
        {persona.name} dials from {VOICE_CALLBACK_CALLER_ID}. About a minute of conversation,
        then we line up the walkthrough.
      </p>

      <div className="mt-6 space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="cb-name" className="block text-sm font-medium text-apple-ink">
            First name
          </label>
          <input
            id="cb-name"
            type="text"
            autoComplete="given-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-apple-divider bg-apple-bg px-4 py-2.5 text-apple-ink outline-none focus:border-apple-blue focus:ring-2 focus:ring-apple-blue/30"
            placeholder="Jordan"
          />
          {touched && !nameValid && (
            <p className="mt-1 text-sm text-red-600">Please enter your first name.</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="cb-phone" className="block text-sm font-medium text-apple-ink">
            Phone number
          </label>
          <input
            id="cb-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-apple-divider bg-apple-bg px-4 py-2.5 text-apple-ink outline-none focus:border-apple-blue focus:ring-2 focus:ring-apple-blue/30"
            placeholder="(414) 310-0078"
          />
          {touched && !phoneValid && (
            <p className="mt-1 text-sm text-red-600">Please enter a valid US phone number.</p>
          )}
        </div>

        {/* Consent — TWO checkboxes (mens-health TCPA pattern). */}
        <label className="flex items-start gap-3 text-sm text-apple-subtext">
          <input
            type="checkbox"
            checked={callConsent}
            onChange={(e) => setCallConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-apple-divider text-apple-blue focus:ring-apple-blue"
          />
          <span>
            I agree to receive a phone call from VentureOS at the number provided. Msg/data
            rates may apply. Reply STOP to opt out.
          </span>
        </label>
        {touched && !callConsent && (
          <p className="-mt-3 text-sm text-red-600">
            We can't call you without your consent.
          </p>
        )}

        <label className="flex items-start gap-3 text-sm text-apple-subtext">
          <input
            type="checkbox"
            checked={textConsent}
            onChange={(e) => setTextConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-apple-divider text-apple-blue focus:ring-apple-blue"
          />
          <span>
            OK to text me at this number (msg/data rates may apply; reply STOP to opt out).
          </span>
        </label>

        {state === 'error' && errorMsg && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={state === 'submitting'}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-apple-blue px-5 py-3 font-medium text-white transition-colors hover:bg-apple-blueHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 disabled:opacity-60 sm:w-auto"
        >
          {state === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Requesting…
            </>
          ) : (
            <>Have {persona.name} call me</>
          )}
        </button>
      </div>
    </motion.form>
  );
};

export default PersonaCallbackForm;
